import { XIcon } from "@wags/ui/product-icons.tsx";

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from "@wags/ui/dialog.tsx";
import type { LocationSelection } from "@/features/viewer-location";
import { MobileSearchLocationSlide } from "../search-controls/box/Mobile/MobileSearchLocationSlide";
import { MobileOverlayViewportFrame } from "@/components/shared/MobileOverlayViewportFrame";
import { useHideAppBehindOverlay } from "@wags/tanstack-start/browser";
import { useIsMobileViewport } from "@wags/tanstack-start/react";

const HEADING = "Where should we look?";
const DESCRIPTION =
    "We use your location to surface dogs near you. No address, just a city-level radius.";

type FindLocationPromptProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    lat?: number;
    lng?: number;
    onLocationSelected: (location: LocationSelection) => void;
};

export function FindLocationPrompt({
    open,
    onOpenChange,
    lat,
    lng,
    onLocationSelected,
}: FindLocationPromptProps) {
    const isMobile = useIsMobileViewport();
    useHideAppBehindOverlay(open && isMobile);

    const locationInput = (
        <MobileSearchLocationSlide
            autoFocus={!isMobile}
            lat={lat}
            lng={lng}
            onLocationChange={onLocationSelected}
        />
    );

    if (!isMobile) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    className="gap-0 p-6 sm:max-w-md"
                    showCloseButton={false}
                    onOpenAutoFocus={(event) => event.preventDefault()}
                >
                    <DialogTitle className="sr-only">
                        Set your location
                    </DialogTitle>
                    <div className="flex items-start justify-between gap-3">
                        <h2 className="text-section-title text-doberman-black text-balance">
                            {HEADING}
                        </h2>
                        <DialogClose
                            aria-label="Close"
                            variant="tile"
                            className="-mr-2"
                        >
                            <XIcon className="size-5" />
                        </DialogClose>
                    </div>
                    {/* oxlint-disable-next-line shadcn/no-arbitrary-values, shadcn/no-restyle -- 32ch measure has no max-width token; the dialog description default is text-sm muted-foreground, not text-body at 60% ink */}
                    <DialogDescription className="mt-2 max-w-[32ch] text-body text-doberman-black/60 text-pretty">
                        {DESCRIPTION}
                    </DialogDescription>
                    <div className="mt-6 flex flex-col gap-1">
                        {locationInput}
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                fullScreen
                showCloseButton={false}
                aria-describedby={undefined}
                onOpenAutoFocus={(event) => event.preventDefault()}
            >
                <DialogTitle className="sr-only">Set your location</DialogTitle>
                <MobileOverlayViewportFrame
                    active={open}
                    className="overflow-hidden bg-true-white pt-[calc(env(safe-area-inset-top)+12px)]"
                >
                    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-6 pb-[calc(env(safe-area-inset-bottom)+24px)]">
                        <div className="flex h-14 shrink-0 items-center justify-end">
                            <DialogClose
                                aria-label="Close"
                                variant="tile"
                                className="-mr-2"
                            >
                                <XIcon className="size-5" />
                            </DialogClose>
                        </div>
                        <div>
                            <h2 className="text-section-title text-doberman-black text-balance">
                                {HEADING}
                            </h2>
                            {/* oxlint-disable-next-line shadcn/no-arbitrary-values -- 32ch measure has no max-width token */}
                            <p className="mt-2 max-w-[32ch] text-body text-doberman-black/60 text-pretty">
                                {DESCRIPTION}
                            </p>
                        </div>
                        <div className="mt-6 flex flex-col gap-1">
                            {locationInput}
                        </div>
                    </div>
                </MobileOverlayViewportFrame>
            </DialogContent>
        </Dialog>
    );
}
